/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {
    Observable,
    throwError
} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AlertNotificationService} from '../../alert-notification.service';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {AlertNotification} from '../../alert.notification.model';
import * as exclude from '../api-interceptor/exclude-endpoints.json';

@Injectable({providedIn: 'root'})
export class APIInterceptor implements HttpInterceptor {

    private static readonly REGEX_FLAGS = 'i';
    private readonly defaultErrorMessage = 'Unexpected error';

    constructor(private clientErrorService: AlertNotificationService) {
    }

    private static ignoreErrorResponse(request: HttpRequest<any>, response: HttpErrorResponse): boolean {
        const url = response.url || request.url;
        for (const endpoint of exclude.endpoints) {
            const regex = new RegExp(endpoint.pattern, APIInterceptor.REGEX_FLAGS);
            if (url && url.search(regex) > -1) {
                if (endpoint.errors == null || endpoint.errors.length === 0) {
                    return true;
                } else {
                    for (const error of endpoint.errors) {
                        if (error.responseStatus === response.status &&
                                (error['codes'] == null ||
                                        response.error != null && error['codes']
                                                .some(x => x.toUpperCase() === response.error.code.toUpperCase()))) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const requestClone = request.clone();
        return next.handle(request)
                .pipe(catchError((error: HttpErrorResponse) => {
                    if (!APIInterceptor.ignoreErrorResponse(requestClone, error)) {
                        const details = error.error != null && error.error.details != null ?
                                error.error.details.map((detail: any) => detail.description) : null;

                        this.clientErrorService.notificationEventEmitter.emit({
                            message: this.getErrorMessage(error),
                            details,
                            alertNotificationSeverity: AlertNotificationSeverity.ERROR
                        } as AlertNotification);
                    }
                    return throwError(error);
                }));
    }

    private getErrorMessage(error: HttpErrorResponse): string {
        let message = this.genericErrorResponse(error);
        if (error.status === 0) {
            message = this.defaultErrorMessage;
        } else if (error.status >= 400 && error.status <= 499 && error.statusText === 'OK') {
            message = this.clientErrorResponse(error);
        }

        return message;
    }

    /**
     * This method handles client errors in the HTTP response. In some cases, certain browsers may incorrectly interpret
     * an error with code 401 as having status text 'OK' instead of 'Unauthorized'. To handle such scenarios, which typically
     * occur for client error responses ranges 400-499, this method returns a more generic error message instead of 'OK', along
     * with the status code.
     */
    private clientErrorResponse(error: HttpErrorResponse): string {
        return this.errorMessageFormat(this.defaultErrorMessage, error.status);
    }

    /**
     * This method handles errors that come in the HTTP response. If the error object contains an error message, it is considered a
     * trustworthy message sent from the server. Otherwise, it generates a generic error message with the status text and status code.
     */
    private genericErrorResponse(error: HttpErrorResponse): string {
        return error.error.message || this.errorMessageFormat(error.statusText, error.status);
    }

    private errorMessageFormat(message: string, code: number): string {
        return `${message} (${code})`;
    }
}
